/**
 * iOS 26 + Chrome: collapsing the bottom toolbar shifts the visual viewport down inside an
 * unchanged layout viewport, so `position: fixed` elements stay where the old bottom was.
 * `visualViewport.offsetTop` is that shift — translating by it cancels the gap exactly.
 *
 * Safari positions correctly on its own
 */

const SELECTOR = '.footer__mobile';

/** One collapsed toolbar plus the home indicator; beyond this the value is not an offset. */
const MAX_OFFSET = 200;

export default function iosBottomAnchor() {
  const vv = window.visualViewport;

  if (!vv || !/CriOS/.test(navigator.userAgent)) {
    return;
  }

  let frame = null;
  let lastApplied = null;

  const apply = () => {
    frame = null;

    // Zoom moves the viewport for unrelated reasons; negative offsets are overscroll.
    const zoomed = vv.scale > 1.01;

    // Rubber-band overscroll drives the value negative; shifting up would lift the
    // element away from an edge it is already sitting on correctly.
    let offset = zoomed
      ? 0
      : Math.max(0, Math.min(window.innerHeight - vv.height, MAX_OFFSET));

    offset = Math.round(offset);

    if (offset === lastApplied) {
      return;
    }
    lastApplied = offset;

    const elements = document.querySelectorAll(SELECTOR);
    const transform = offset ? `translate3d(0, ${offset}px, 0)` : '';

    /* eslint-disable no-param-reassign */
    elements.forEach((el) => {
      el.style.transform = transform;
    });
    /* eslint-enable no-param-reassign */
  };

  const schedule = () => {
    if (frame === null) {
      frame = requestAnimationFrame(apply);
    }
  };

  // scroll carries the toolbar transition; resize carries its start and end.
  vv.addEventListener('scroll', schedule);
  vv.addEventListener('resize', schedule);
  window.addEventListener('orientationchange', schedule);

  // An invisible fixed-position element
  const sentinel = document.createElement('div');
  sentinel.setAttribute('aria-hidden', 'true');
  sentinel.style.cssText = 'position:fixed;bottom:0;left:0;width:1px;height:1px;'
    + 'opacity:0;pointer-events:none;overflow:hidden;';
  document.body.appendChild(sentinel);

  let counter = 0;

  const tick = () => {
    apply();

    // The value is irrelevant; it only has to differ from the previous frame's.
    counter = (counter + 1) % 1000;
    sentinel.textContent = counter;

    requestAnimationFrame(tick);
  };

  requestAnimationFrame(tick);
}
